import { STORAGE_ENDED_ACTION, STORAGE_FORM_INFO } from '../components/common/Storage';
import {
  LEARNOSITY_EMIT_TOOLUSED,
  TESTER_SAVEANDEXIT,
  TESTER_SAVEANDEXIT_ERROR,
} from '../context/PwaEvents';
import { ALLOWED_LANGUAGES, DEFAULT_LANGUAGE } from '../i18n';
import { getFromStorage, setInStorage } from './common/StorageHelper';
import {
  multiChoiceLabelPrefix,
  answerMasking,
  speechStream
} from '@nmc/learnosity-extensions';

export const LEARNOSITY_EVENTS = {
  TEST_FINISHED_SAVE: 'test:finished:save',
  TEST_FINISHED_SUBMIT: 'test:finished:submit',
  TEST_SUBMIT_SUCCESS: 'test:submit:success',
  TEST_SUBMIT_ERROR: 'test:submit:error',
  TEST_SUBMIT: 'test:submit',
  TEST_ACTIVITYJSON: 'test:getActivityJSON',
  TEST_SUBMIT_PROGRESS: 'test:submit:progress',
  TEST_FINISHED_DISCARD: 'test:finished:discard',
  TEST_PROCTOR_EXIT_AND_SUBMIT: 'test:proctor:exitAndSubmit',
  TEST_PROCTOR_SAVE: 'test:proctor:save',
  TEST_PROCTOR_TERMINATED: 'test:proctor:terminated',
  TEST_READY: 'test:ready',
  TEST_SAVE_ERROR: 'test:save:error',
  TEST_SAVE_SUCCESS: 'test:save:success',
  TEST_PAUSE: 'test:pause',
  TEST_RESUME: 'test:resume',
  ITEM_LOAD: 'item:load',
  TEST_START: 'test:start',
  ITEM_BEFOREUNLOAD: 'item:beforeunload',
};

export const LEARNOSITY_TOOLS = {
  ACCESSIBILITY: 'Accessibility',
  CALCULATOR: 'Calculator',
  LINE_READER: 'LineReader',
  NOTEPAD: 'Notepad',
  ANSWER_ELIMINATOR: 'AnswerEliminator',
  ANSWER_MASKING: 'AnswerMasking',
  TEXT_TO_SPEECH: 'TextToSpeech',
  SAVEANDEXIT: 'SaveAndExit'
};

const getToolConfig = (pnp) => {
  let config = {};
  if (pnp?.answerMasking?.disable) {
    config.disableAnswerMasking = true;
  }
  if (!pnp?.spoken?.activateByDefault) {
    config.disableTextToSpeech = true;
  }
  return config;
};

function attachEventHandlerForToolsUsed(itemsApp, eventEmitter) {
  console.log('Attaching button click event listeners has begun');
  /**
   * Create an array of each button element you'd like to track,
   * by it's name and it's DOM selector in the assessment player
   */
  const BUTTON_ELEMENTS = [
    { name: LEARNOSITY_TOOLS.CALCULATOR, selector: '.lrn_btn.lrn-calc-toggle' },
    {
      name: LEARNOSITY_TOOLS.LINE_READER,
      selector: '.lrn_btn.lrn_linereader-toggle',
    },
    { name: LEARNOSITY_TOOLS.NOTEPAD, selector: '.lrn_btn.lrn_btn_notepad' },
    {
      name: LEARNOSITY_TOOLS.ANSWER_ELIMINATOR,
      selector: '.lrn_btn.test-answer-masking',
    },
    {
      name: LEARNOSITY_TOOLS.ANSWER_MASKING,
      selector: '.lrn_btn.custom_btn.AnswerMasking_btn',
    },
    {
      name: LEARNOSITY_TOOLS.TEXT_TO_SPEECH,
      selector: '.lrn_btn.custom_btn.TextToSpeech_btn',
    },
  ];
  /**
   * Attach a click event listener to each button element by it's selector to log
   * out when the button by that name has been clicked
   *
   * You can add further logic to handle what should happen for each button name, if needed
   */
  BUTTON_ELEMENTS.forEach((element) => {
    const { name, selector } = element;
    const button = document.querySelector(selector);
    if (button) {
      button.addEventListener('click', () => {
        if (name === LEARNOSITY_TOOLS.ANSWER_ELIMINATOR) {
          eventEmitter({
            appInstance: itemsApp,
            eventName: LEARNOSITY_EMIT_TOOLUSED,
            detail: {
              tool: name,
              toggleOn: button.getAttribute('aria-pressed') || false,
            },
          });
        } else if (name === LEARNOSITY_TOOLS.ANSWER_MASKING) {
          const isActive = button.classList?.contains('lrn_active') || false;
          eventEmitter({
            appInstance: itemsApp,
            eventName: LEARNOSITY_EMIT_TOOLUSED,
            detail: {
              tool: name,
              toggleOn: isActive,
            },
          });
        } else {
          eventEmitter({
            appInstance: itemsApp,
            eventName: LEARNOSITY_EMIT_TOOLUSED,
            detail: { tool: name },
          });
        }
      });
    }
  });

  itemsApp.on('accessibility:change:color', function (colorPalette) {
    const paletteElement = document.querySelector(
      '.lrn-access-contrast-palette'
    );
    const selectedColorElement = paletteElement.querySelector(
      `.${colorPalette}`
    );
    eventEmitter({
      appInstance: itemsApp,
      eventName: LEARNOSITY_EMIT_TOOLUSED,
      detail: {
        tool: LEARNOSITY_TOOLS.ACCESSIBILITY,
        type: 'Color',
        selected: selectedColorElement?.textContent
          ?.replace(/[\n\r\t]/gm, '')
          ?.trim(),
      },
    });
  });
  itemsApp.on('accessibility:change:fontsize', function (fontSize) {
    eventEmitter({
      appInstance: itemsApp,
      eventName: LEARNOSITY_EMIT_TOOLUSED,
      detail: {
        tool: LEARNOSITY_TOOLS.ACCESSIBILITY,
        type: 'Font',
        selected: fontSize,
      },
    });
  });
}

const addEventHandlerForCustomSave = (itemsApp, eventEmitter) => {
  itemsApp.on('button:nmc_save_button:clicked', function () {
    const isPreview = window.location?.pathname?.startsWith('/authorform');
    if (isPreview) {
      eventEmitter({
        appInstance: itemsApp,
        eventName: TESTER_SAVEANDEXIT,
      });
    } else {
      setInStorage(STORAGE_ENDED_ACTION, TESTER_SAVEANDEXIT);
      itemsApp.save({
        success: () => {
          eventEmitter({
            appInstance: itemsApp,
            eventName: TESTER_SAVEANDEXIT,
          });
        },
        error: () => {
          itemsApp.once(LEARNOSITY_EVENTS.TEST_SAVE_SUCCESS, () => {
            eventEmitter({
              appInstance: itemsApp,
              eventName: TESTER_SAVEANDEXIT,
            });
          });
          eventEmitter({
            appInstance: itemsApp,
            eventName: TESTER_SAVEANDEXIT_ERROR,
          });
        },
      });
    }
  });
};

const attachEventHandlers = (itemsApp, eventEmitter, toolConfig) => {
  //Let's attach the render related learnosity extensions now.
  //we cannot wait until ready since it will be too late for first load.
  multiChoiceLabelPrefix.run(itemsApp);
  //add events here.
  Object.values(LEARNOSITY_EVENTS).forEach((event) => {
    itemsApp.on(event, function () {
      eventEmitter({
        appInstance: itemsApp,
        eventName: event,
      });
    });
  });
  addEventHandlerForCustomSave(itemsApp, eventEmitter);
  if (!toolConfig?.disableAnswerMasking) {
    //This adds event handler for the answer masking tool.
    answerMasking.run(itemsApp);
  }
  if (!toolConfig?.disableTextToSpeech) {
    speechStream.run(itemsApp);
  }
};

const configureToolsVisibility = (toolConfig) => {
  let answerMaskingButton =
    document.getElementsByClassName('AnswerMasking_btn');
  if (answerMaskingButton?.[0] && toolConfig?.disableAnswerMasking) {
    answerMaskingButton[0].remove();
  }
  //Text-to-Speech
  let textToSpeechButton = document.getElementsByClassName('TextToSpeech_btn');
  if (textToSpeechButton?.[0] && toolConfig?.disableTextToSpeech) {
    textToSpeechButton[0].remove();
  }
};

const handleLanguageOverridesForCustomButtons = (labelBundle) => {
  const BUTTON_ELEMENTS = [
    {
      name: LEARNOSITY_TOOLS.SAVEANDEXIT,
      selector: 'custom_btn test-save',
      key: 'saveQuit',
    },
    {
      name: LEARNOSITY_TOOLS.ANSWER_MASKING,
      selector: 'AnswerMasking_btn',
      key: 'nmcAnswerMasking',
    },
    {
      name: LEARNOSITY_TOOLS.TEXT_TO_SPEECH,
      selector: 'TextToSpeech_btn',
      key: 'nmcTextToSpeech',
    },
  ];
  BUTTON_ELEMENTS.forEach((element) => {
    const { selector, key } = element;
    const button = document.getElementsByClassName(selector);
    const override = labelBundle[key];
    if (button?.[0] && override) {
      button?.[0].setAttribute('data-original-title', override);
      button?.[0].setAttribute('aria-label', override);

      // Find and update the btn-label child element if it exists
      const btnLabel = button[0].querySelector('.btn-label');
      if (btnLabel) {
        btnLabel.textContent = override;
      }
    }
  });
};

const setupLanguageTags = (pnpLangProp, formLangProp) => {
  let testerLang = DEFAULT_LANGUAGE; // en-US by default if pnpLanguage is not passed
  if (pnpLangProp) {
    const pnpLangMatch = ALLOWED_LANGUAGES.find(
      (language) => language.toLowerCase() === pnpLangProp.toLowerCase(),
    );
    if (pnpLangMatch !== DEFAULT_LANGUAGE) {
      testerLang = pnpLangMatch;
      let rootElement = document.getElementById('root');
      if (rootElement) {
        rootElement.lang = testerLang;
      }
    }
  }

  let formLang = DEFAULT_LANGUAGE; // en-US by default if formLanguage is not passed
  if (formLangProp) {
    const formLangMatch = ALLOWED_LANGUAGES.find(
      (language) => language.toLowerCase() === formLangProp.toLowerCase(),
    );
    if (formLangMatch !== formLang) {
      formLang = formLangMatch;
    }
  }

  if (formLang !== testerLang) {
    //only we add lang tags in learnosity else it will inherit
    let learnosityItems = document.querySelectorAll('.learnosity-item');
    learnosityItems.forEach((item) => {
      item.lang = formLang;
    });
  }
};

const initItemsApp = (
  request,
  setLearnosityComponentLoading,
  learnosityComponentCallback,
  eventEmitter,
  pnp
) => {
  setLearnosityComponentLoading(true);
  let toolConfig = getToolConfig(pnp);
  var itemsApp = window.LearnosityItems.init(request, {
    readyListener() {
      console.log('Learnosity Author API is ready');
      configureToolsVisibility(toolConfig);
      setLearnosityComponentLoading(false);
      learnosityComponentCallback({ itemsApp: itemsApp, ready: true });
      //Learnosity container should be ready to attach tool used event
      //it depends on DOM elements to attach the events
      attachEventHandlerForToolsUsed(itemsApp, eventEmitter);
      if (pnp?.language) {
          handleLanguageOverridesForCustomButtons(request?.request?.config?.labelBundle)
      }
      let formInfo = getFromStorage(STORAGE_FORM_INFO);
      setupLanguageTags(pnp?.language, formInfo?.formLanguage);
    },
    errorListener(e) {
      //callback to occur on error
      console.log(e);
      learnosityComponentCallback({ itemsApp: itemsApp, error: e });
      setLearnosityComponentLoading(false);
    },
  });
  // attach events here so that test:ready, test:load and test:pause
  // event gets triggered when the container is ready
  attachEventHandlers(itemsApp, eventEmitter, toolConfig);
  return itemsApp;
};

const resetItemsApp = (itemsApp) => {
  if (itemsApp) {
    try {
      console.log('Resetting itemsApp for reinitialization');
      itemsApp.off();
      itemsApp.reset();
    } catch (error) {
      console.log(error);
    }
  }
};

/**
 * Returns button array.
 * [ {
 *    button_id: unique_html_button_id,
 *    label: buttonLabel,
 *    is_primary: isItAPrimaryButton
 * }]
 * Also registers the button click action with itemApp object
 *
 * @param {*} actions Expected format is
 * [{
 *     id: unique_html_button_id,
 *     primary: isItAPrimaryButton_true/false
 *     handle: 'A method to callback on button click'
 * }]
 * @param {*} appInstance learnosity's itemApp object
 * @returns button array
 */
const getCustomDialogButtons = (actions, appInstance) => {
  let buttons = [];
  for (let action of actions) {
    const button = {
      button_id: action.id,
      label: action.label,
      is_primary: action.primary,
    };
    appInstance?.assessApp()?.on(`button:${action.id}:clicked`, function () {
      action.handle();
    });
    buttons.push(button);
  }
  return buttons;
};

const showCustomDialog = (appInstance, dialogDetails = {}, currentlyPaused) => {
  //added additional condition. for case: tester paused but TA is out of sync and TA issued proctor paused: if we call custom.show, it will trigger
  //a resume and a pause again., this new param will prevent extra messages being triggered/sent
  if (!currentlyPaused) {
    appInstance
      ?.assessApp()
      ?.dialogs()
      ?.custom?.show({ ...dialogDetails });
  }
  const learnosityContainer = document.getElementById('learnosity_assess');
  learnosityContainer.style.display = 'none';
  return;
};

const showCustomDialogWithCountdown = (
  appInstance,
  dialogDetails = {},
  countdownSeconds,
  closeDialogWhenDone,
  callback
) => {
  let dialogDetailsWithCountdown = {
    ...dialogDetails,
    body: dialogDetails.body.replace(
      '$COUNTDOWN_SECONDS_PLACEHOLDER',
      `<span id="custom_dialog_countdown">${countdownSeconds}</span>`
    ),
  };
  appInstance?.dialogs()?.custom?.show({ ...dialogDetailsWithCountdown });
  var currentCount = countdownSeconds;
  var countdownElem = document.getElementById('custom_dialog_countdown');
  var timer = setInterval(function () {
    if (currentCount === 0) {
      clearInterval(timer);
    }
    if (!countdownElem) {
      countdownElem = document.getElementById('custom_dialog_countdown');
    }
    if (countdownElem) {
      countdownElem.innerHTML = currentCount;
    }
    currentCount--;
  }, 1000);
  setTimeout(() => {
    if (closeDialogWhenDone) {
      appInstance?.dialogs()?.custom?.hide();
    }
    if (callback) {
      callback();
    }
  }, countdownSeconds * 1000);
};

const hideCustomDialog = (appInstance, callback) => {
  appInstance?.assessApp()?.dialogs()?.custom?.hide();
  setTimeout(() => {
    const learnosityContainer = document.getElementById('learnosity_assess');
    learnosityContainer.style.display = 'inherit';
    window.dispatchEvent(new Event('resize'));
    if (callback) {
      callback();
    }
  }, 500);
  return;
};

export {
  initItemsApp,
  resetItemsApp,
  getCustomDialogButtons,
  showCustomDialog,
  hideCustomDialog,
  showCustomDialogWithCountdown,
};
